import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Download, LockKeyhole, Mail, Mountain, Send, Sparkles } from 'lucide-react';
import { Section } from './components/Section';
import { ProjectCard } from './components/ProjectCard';
import { projects } from './content/projects';
import { thinkingNotes } from './content/thinking';
import { buyerAudiences, buyerProblem, enterpriseProof, proofPoints, services, siteMeta, voicePrinciples, workModes } from './content/site';
import { credentials } from './content/resume';
import { validateContactSubmission, type ContactReason, type ContactSubmission } from './contact';
import { draftColbyResponse } from './colbyWorkflow';
import { createLeadRecord, saveLeadRecord } from './cccLeadStore';
import { track } from './analytics';

type PageId = 'home' | 'work' | 'projects' | 'projectDetail' | 'thinking' | 'about' | 'contact' | 'credentials';

type NavItem = { label: string; page: PageId; path: string };

const navItems: NavItem[] = [
  { label: 'Services', page: 'work', path: '/services' },
  { label: 'Projects', page: 'projects', path: '/projects' },
  { label: 'Thinking', page: 'thinking', path: '/thinking' },
  { label: 'About', page: 'about', path: '/about' }
];

const pathToPage = new Map<string, PageId>([
  ['/', 'home'],
  ['/work', 'work'],
  ['/services', 'work'],
  ['/projects', 'projects'],
  ['/thinking', 'thinking'],
  ['/about', 'about'],
  ['/contact', 'contact'],
  ['/credentials', 'credentials']
]);

const emptyContact: ContactSubmission = {
  name: '',
  email: '',
  organization: '',
  reason: 'serious-work',
  message: '',
  areaOfInterest: '',
  website: '',
  challengeAnswer: ''
};

function currentPage(): PageId {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (/^\/projects\/[^/]+$/.test(path) && projects.some((project) => `/projects/${project.id}` === path)) return 'projectDetail';
  return pathToPage.get(path) || 'home';
}

function pathFor(page: PageId) {
  if (page === 'home') return '/';
  if (page === 'projectDetail') return '/projects';
  return navItems.find((item) => item.page === page)?.path || '/';
}

function usePage() {
  const [page, setPage] = useState<PageId>(() => currentPage());
  useEffect(() => {
    const onPop = () => setPage(currentPage());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  function navigate(next: PageId) {
    const path = pathFor(next);
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    track('navigation', { page: next });
  }
  return { page, navigate };
}

function PageLink({ page, className, children }: { page: PageId; className?: string; children: React.ReactNode }) {
  const { navigate } = usePageContext();
  return <a className={className} href={pathFor(page)} onClick={(event) => { event.preventDefault(); navigate(page); }}>{children}</a>;
}

let pageContext: { page: PageId; navigate: (page: PageId) => void } | null = null;
function usePageContext() {
  if (!pageContext) throw new Error('Page context missing');
  return pageContext;
}

function Header() {
  const { page, navigate } = usePageContext();
  return (
    <header className="site-header" aria-label="Primary">
      <a className="brand" href="/" aria-label="Greg Cothran home" onClick={(event) => { event.preventDefault(); navigate('home'); }}>
        <span className="brand-mark">GC</span>
        <span>Greg Cothran</span>
      </a>
      <nav>
        {navItems.map((item) => (
          <a key={item.page} href={item.path} aria-current={page === item.page || (page === 'projectDetail' && item.page === 'projects') ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate(item.page); }}>
            {item.label}
          </a>
        ))}
      </nav>
      <a className="header-cta" href="/contact" onClick={(event) => { event.preventDefault(); navigate('contact'); }}>
        <Mail size={17} aria-hidden="true" /> Start a Conversation
      </a>
    </header>
  );
}

function PageIntro({ eyebrow, title, lead }: { eyebrow: string; title: string; lead: string }) {
  return (
    <section className="page-intro">
      <PageLink page="home" className="back-home"><ArrowLeft size={17} aria-hidden="true" /> Home</PageLink>
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p className="hero-lead">{lead}</p>
    </section>
  );
}

function PageFeatureImage({ src, alt }: { src: string; alt: string }) {
  return (
    <section className="page-feature-image">
      <img src={src} alt={alt} />
    </section>
  );
}

function ProofStrip() {
  return (
    <section className="proof-strip" aria-label="Selected proof points">
      {proofPoints.map((point) => <div key={point.label}><strong>{point.value}</strong><span>{point.label}</span><p>{point.detail}</p></div>)}
      <blockquote>Build the right things. Make them work. Make them last.</blockquote>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <section className="hero executive-hero" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="eyebrow">Greg Cothran</p>
          <h1 id="hero-title">Ideas to reality, built with <em>mission discipline.</em></h1>
          <p className="offer-line">{siteMeta.offerLine}</p>
          <p className="hero-lead">{siteMeta.shortPositioning}</p>
          <div className="hero-actions">
            <PageLink page="contact" className="primary-action">Discuss AI Consulting <ArrowRight size={18} aria-hidden="true" /></PageLink>
            <PageLink page="projects" className="secondary-action">View Projects</PageLink>
          </div>
          <p className="precision-line"><span>People, technology, and operations.</span><span>Clear thinking.</span><span>Working results.</span></p>
        </div>
        <div className="hero-visual" aria-label="Mountain identity image">
          <img src="/assets/greg-mountain-hero.png?v=20260927" alt="Greg on a mountain overlook wearing a Cognitive Workstead hoodie at sunset" />
        </div>
      </section>
      <ProofStrip />
      <section className="buyer-problem" aria-labelledby="buyer-problem-title"><div><p className="eyebrow">AI Transformation</p><h2 id="buyer-problem-title">{buyerProblem.title}</h2><p className="section-lead">{buyerProblem.lead}</p></div><blockquote>{buyerProblem.point}</blockquote></section>
      <section className="services-section" aria-labelledby="services-title"><div className="section-heading compact"><p className="eyebrow">How I Help</p><h2 id="services-title">Fractional Chief AI Officer (CAIO), AI strategy, and governed operational delivery.</h2></div><div className="services-grid">{services.map((service) => <div key={service.title}><span></span><strong>{service.title}</strong><p>{service.body}</p></div>)}</div></section>
      <section className="enterprise-proof" aria-label="Enterprise proof points">{enterpriseProof.map((point) => <div key={point.label}><strong>{point.value}</strong><span>{point.label}</span><p>{point.detail}</p></div>)}</section>
      <section className="conversion-band" aria-labelledby="conversion-title">
        <div>
          <p className="eyebrow">Next Step</p>
          <h2 id="conversion-title">Need governed AI capability that can hold up in real operations?</h2>
          <p>Bring Greg in when the goal is bigger than a demo: practical AI strategy, authority boundaries, private implementation patterns, delivery discipline, and measurable adoption.</p>
        </div>
        <PageLink page="contact" className="primary-action">Discuss AI Consulting <ArrowRight size={18} aria-hidden="true" /></PageLink>
      </section>
    </>
  );
}

function WorkPage() {
  return (
    <>
      <PageIntro eyebrow="Services" title="Turning unclear problems into working systems." lead="Greg helps teams clarify the mission, structure the work, and move fast without losing trust or control." />
      <PageFeatureImage src="/assets/services-field-desk.png?v=20260928" alt="Greg planning structured work at an outdoor table overlooking mountains at sunset" />
      <Section title="The Working Pattern" lead="Clarify the mission. Structure the system. Govern execution. Keep improving until it earns trust.">
        <div className="work-grid executive-grid">{workModes.map((mode) => <p key={mode}>{mode}</p>)}</div>
      </Section>
      <section className="services-section inline-services" aria-labelledby="services-page-title"><div className="section-heading compact"><p className="eyebrow">Engage Greg</p><h2 id="services-page-title">Four practical ways to use the work.</h2></div><div className="services-grid">{services.map((service) => <div key={service.title}><span></span><strong>{service.title}</strong><p>{service.body}</p></div>)}</div></section>
      <Section title="Who I Help" lead="A practical path for leaders who need AI to become capability, not theater.">
        <div className="audience-list">{buyerAudiences.map((audience, index) => <div key={audience}><span>{String(index + 1).padStart(2, '0')}</span><strong>{audience}</strong></div>)}</div>
      </Section>
      <ProofStrip />
    </>
  );
}

function ProjectsPage() {
  const clientProof = projects.filter((project) => project.tier === 'client-proof');
  const labs = projects.filter((project) => project.tier === 'lab');
  return (
    <>
      <PageIntro eyebrow="Projects" title="Systems built to work and hold up." lead="A focused view of Greg's work across AI, software delivery, executive advisory, and resilient infrastructure." />
      <section className="client-meaning page-body"><p className="eyebrow">What This Means for Clients</p><p>These systems are working examples of the services Greg brings to clients: AI governance, private AI, evaluation harnesses, automation, operating model design, and dependable delivery.</p></section>
      <section className="project-group" aria-labelledby="client-proof-title"><div className="section-heading compact"><p className="eyebrow">Client-Relevant Projects</p><h2 id="client-proof-title">The work most directly tied to consulting value.</h2></div><div className="projects-page-grid">{clientProof.map((project, index) => <ProjectCard key={project.id} project={project} index={index} />)}</div></section>
      <section className="project-group muted-group" aria-labelledby="lab-title"><div className="section-heading compact"><p className="eyebrow">Labs and Future Concepts</p><h2 id="lab-title">Useful signals, kept in the right weight class.</h2></div><div className="projects-page-grid secondary-grid">{labs.map((project, index) => <ProjectCard key={project.id} project={project} index={clientProof.length + index} />)}</div></section>
    </>
  );
}

function ProjectDetailPage() {
  const projectId = window.location.pathname.split('/').filter(Boolean)[1];
  const project = projects.find((item) => item.id === projectId);
  if (!project) {
    return (
      <>
        <PageIntro eyebrow="Projects" title="Project not found." lead="Return to the projects index to review the current work." />
        <section className="page-body"><PageLink page="projects" className="secondary-action">Back to Projects</PageLink></section>
      </>
    );
  }
  return (
    <>
      <section className="page-intro project-detail-intro">
        <PageLink page="projects" className="back-home"><ArrowLeft size={17} aria-hidden="true" /> Projects</PageLink>
        <p className="eyebrow">{project.kicker}</p>
        <h1>{project.title}</h1>
        <p className="hero-lead">{project.detail.headline}</p>
      </section>
      <section className="project-detail page-body">
        <div className="project-detail-media"><img src={project.image} alt={project.imageAlt} /></div>
        <div className="project-detail-copy">
          <p className="eyebrow">Why It Matters</p>
          <h2>{project.detail.headline}</h2>
          <p>{project.detail.thesis}</p>
          <div className="project-detail-sections">{project.detail.sections.map((section) => <article key={section.title}><h3>{section.title}</h3><p>{section.body}</p></article>)}</div>
          <div className="outcome-panel"><p className="eyebrow">Working Evidence</p><div className="project-detail-list">{project.detail.outcomes.map((item) => <span key={item}>{item}</span>)}</div></div>
          <aside className="client-relevance"><h3>Client relevance</h3><p>{project.detail.clientRelevance}</p><small>{project.safeDisclosure}</small></aside>
          <PageLink page="contact" className="primary-action">Discuss related work <ArrowRight size={18} aria-hidden="true" /></PageLink>
        </div>
      </section>
    </>
  );
}

function ThinkingPage() {
  return (
    <>
      <PageIntro eyebrow="Thinking" title="Thinking That Turns Into Systems" lead="Short notes on the ideas that keep showing up in serious work: judgment, AI, resilience, governance, and execution." />
      <PageFeatureImage src="/assets/thinking-mountain-squat.png?v=20260928" alt="Greg crouched on a mountain overlook at sunset, looking across the terrain" />
      <section className="thinking-grid page-grid">{thinkingNotes.map((post) => <article key={post.slug}><span>{post.theme}</span><h3>{post.title}</h3><p>{post.deck}</p></article>)}</section>
    </>
  );
}

function AboutPage() {
  return (
    <>
      <PageIntro eyebrow="About" title="Calm, practical work for consequential problems." lead="Greg has led enterprise technology portfolios, advised senior clients, supported wartime command-and-control operations, and built practical AI systems from the ground up." />
      <section className="about-layout page-body"><div><p>Greg works where the problem is too important for theater and too complex for slogans. He brings mission discipline, practical judgment, and follow-through to ideas that need to become real.</p><p>His background runs through Air Force mission systems, enterprise technology operations, global managed services, AI transformation, and founder-level build work. The common thread is practical: understand the real operating problem, give it structure, then keep improving until people can trust the system.</p><div className="voice-list">{voicePrinciples.map((item) => <span key={item}>{item}</span>)}</div></div><aside className="about-side"><img className="about-portrait" src="/assets/greg-mountain-hero.png?v=20260927" alt="Greg standing on a mountain overlook at sunset" /><div className="identity-card"><Mountain size={22} aria-hidden="true" /><h3>Higher standards. Wider terrain.</h3><p>The mountain is a visual identity, not a claim of a literal photo. It points to the standard behind the work: perspective, endurance, and a bias toward problems worth climbing for.</p></div></aside></section>
    </>
  );
}

function CredentialsPage() {
  const [credentialEntry, setCredentialEntry] = useState('');
  const normalizedEntry = credentialEntry.trim().toUpperCase();
  const credentialsOpen = credentials.acceptedInitials.includes(normalizedEntry);
  return (
    <>
      <PageIntro eyebrow="Credentials" title="Credentials for Deeper Evaluation" lead="A traditional resume is available for recruiters and focused hiring conversations." />
      <section className="credentials-page"><div className="credentials-panel"><div className="credentials-title"><LockKeyhole size={18} aria-hidden="true" /><h3>{credentials.label}</h3></div><p>{credentials.note}</p><label><span>{credentials.prompt}</span><input value={credentialEntry} onChange={(event) => setCredentialEntry(event.target.value)} maxLength={4} autoComplete="off" /></label>{credentialsOpen ? <a className="download-link" href="/credentials/Greg-Cothran-Resume.pdf" rel="nofollow" onClick={() => track('resume_link_opened')}><Download size={17} aria-hidden="true" /> Download resume</a> : <p className="small-note">Enter initials to reveal the resume link.</p>}</div></section>
    </>
  );
}

function ContactPage() {
  const [contact, setContact] = useState<ContactSubmission>(emptyContact);
  const [contactState, setContactState] = useState<'idle' | 'error' | 'saved'>('idle');
  function updateContact<K extends keyof ContactSubmission>(key: K, value: ContactSubmission[K]) { setContact((current) => ({ ...current, [key]: value })); setContactState('idle'); }
  function submitContact(event: React.FormEvent<HTMLFormElement>) { event.preventDefault(); const result = validateContactSubmission(contact); if (!result.ok) { setContactState('error'); track('contact_guard_failed', { errors: result.errors.join(',') }); return; } const response = draftColbyResponse(contact); const record = createLeadRecord(contact, response); saveLeadRecord(record); setContactState('saved'); setContact(emptyContact); track('contact_lead_created', { reason: record.submission.reason, month: record.monthKey }); }
  return (
    <>
      <PageIntro eyebrow="Contact" title="Start the Right Conversation" lead="For serious conversations about mission work, AI systems, software delivery, partnerships, or hard problems worth structuring." />
      <section className="contact-layout page-body"><form onSubmit={submitContact} className="contact-form"><input className="hidden-field" tabIndex={-1} autoComplete="off" value={contact.website} onChange={(event) => updateContact('website', event.target.value)} aria-hidden="true" /><label><span>Name</span><input value={contact.name} onChange={(event) => updateContact('name', event.target.value)} /></label><label><span>Email</span><input type="email" value={contact.email} onChange={(event) => updateContact('email', event.target.value)} /></label><label><span>Organization</span><input value={contact.organization} onChange={(event) => updateContact('organization', event.target.value)} /></label><label><span>Reason</span><select value={contact.reason} onChange={(event) => updateContact('reason', event.target.value as ContactReason)}><option value="serious-work">AI consulting conversation</option><option value="mission">Mission or government conversation</option><option value="partnership">Business partnership</option><option value="speaking">Speaking or writing</option><option value="other">Something else</option></select></label><label><span>Area of interest</span><input value={contact.areaOfInterest} onChange={(event) => updateContact('areaOfInterest', event.target.value)} /></label><label className="full"><span>Message</span><textarea value={contact.message} onChange={(event) => updateContact('message', event.target.value)} rows={5} /></label><label><span>Simple check: type mission</span><input value={contact.challengeAnswer} onChange={(event) => updateContact('challengeAnswer', event.target.value)} /></label><button type="submit"><Send size={17} aria-hidden="true" /> Send to Greg</button>{contactState === 'error' ? <p className="form-state error">Please check the required fields and simple check.</p> : null}{contactState === 'saved' ? <p className="form-state success">Received. Greg will read it before any response.</p> : null}</form><aside className="next-panel"><div><Sparkles size={18} aria-hidden="true" /><h3>What Happens Next</h3></div><pre>{contactState === 'saved' ? 'Your note has been captured. If there is a fit, the follow-up will be deliberate and human-read before it goes out.' : 'Use the form for AI transformation, fractional Chief AI Officer (CAIO) support, governance, automation, partnerships, or focused technical discussion. Greg reads messages before any reply.'}</pre></aside></section>
    </>
  );
}

function MainPage({ page }: { page: PageId }) {
  if (page === 'work') return <WorkPage />;
  if (page === 'projects') return <ProjectsPage />;
  if (page === 'projectDetail') return <ProjectDetailPage />;
  if (page === 'thinking') return <ThinkingPage />;
  if (page === 'about') return <AboutPage />;
  if (page === 'contact') return <ContactPage />;
  if (page === 'credentials') return <CredentialsPage />;
  return <HomePage />;
}

function Footer() {
  return (
    <footer>
      <span>Greg Cothran</span>
      <span>Ideas to Reality x Mission Discipline</span>
      <nav aria-label="Footer">
        <PageLink page="credentials">Credentials</PageLink>
        <PageLink page="contact">Contact</PageLink>
        <a href="https://www.cognitiveworkstead.com/" target="_blank" rel="noreferrer">Cognitive Workstead</a>
        <a href="https://www.linkedin.com/in/cothrang" target="_blank" rel="noreferrer">LinkedIn</a>
      </nav>
      <Mountain size={18} aria-hidden="true" />
    </footer>
  );
}

export function App() {
  const ctx = usePage();
  pageContext = ctx;
  useEffect(() => { track('page_view', { surface: ctx.page }); }, [ctx.page]);
  return <div className="site-shell"><Header /><main><MainPage page={ctx.page} /></main><Footer /></div>;
}
